import React, { useState, useRef } from 'react'
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { PulseLoader } from 'react-spinners';

const API_URL = process.env.REACT_APP_API_URL;

const SoData = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [importResults, setImportResults] = useState(null);
    const fileInputRef = useRef(null);

    // Convert file to base64
    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            // Check if file is valid
            if (!file) {
                reject(new Error("No file selected"));
                return;
            }

            // Check file size (limit to 10MB)
            const maxSize = 10 * 1024 * 1024; // 10MB
            if (file.size > maxSize) {
                reject(new Error("File size exceeds 10MB limit"));
                return;
            }

            if (file.size === 0) {
                reject(new Error("File is empty or cannot be read"));
                return;
            }

            const reader = new FileReader();
            
            reader.onload = () => {
                try {
                    // Remove data URL prefix
                    const base64String = reader.result.split(',')[1];
                    if (!base64String) {
                        reject(new Error("Failed to convert file to base64"));
                        return;
                    }
                    resolve(base64String);
                } catch (error) {
                    reject(new Error("Error processing file: " + error.message));
                }
            };
            
            reader.onerror = (error) => {
                let errorMessage = "Failed to read file. ";
                if (error.target && error.target.error) {
                    const errorCode = error.target.error.code;
                    const NOT_FOUND_ERR = 1;
                    const NOT_READABLE_ERR = 2;
                    const ABORT_ERR = 3;
                    
                    switch (errorCode) {
                        case NOT_FOUND_ERR:
                            errorMessage += "File not found.";
                            break;
                        case NOT_READABLE_ERR:
                            errorMessage += "File is not readable. Please ensure the file is not open in another program and try again.";
                            break;
                        case ABORT_ERR:
                            errorMessage += "File reading was aborted.";
                            break;
                        default:
                            errorMessage += "An error occurred while reading the file. Please try selecting the file again.";
                    }
                } else {
                    errorMessage += "Please try selecting the file again.";
                }
                reject(new Error(errorMessage));
            };

            reader.onabort = () => {
                reject(new Error("File reading was aborted"));
            };

            try {
                reader.readAsDataURL(file);
            } catch (error) {
                reject(new Error("Failed to start reading file: " + error.message));
            }
        });
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Check if file is CSV format
            const validExtensions = ['.csv'];
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
            
            if (!validExtensions.includes(fileExtension)) {
                toast.error("Please upload a CSV file (.csv)", { autoClose: 2000 });
                event.target.value = '';
                setSelectedFile(null);
                return;
            }

            setSelectedFile(file);
            setImportResults(null);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!selectedFile) {
            toast.error("Please select a CSV file to import", { autoClose: 2000 });
            return;
        }

        setIsLoading(true);
        setImportResults(null);

        try {
            // Convert file to base64
            let fileBase64;
            try {
                fileBase64 = await fileToBase64(selectedFile);
            } catch (fileError) {
                toast.error(fileError.message || "Failed to read file. Please try again.", { autoClose: 3000 });
                setIsLoading(false);
                // Reset file input on error
                setSelectedFile(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                return;
            }

            const payload = {
                action: "import-zoho",
                fileBase64: fileBase64,
                fileName: selectedFile.name || "zoho_items.csv"
            };

            const response = await axios.post(API_URL, payload);

            // Parse the response - handle both string and object responses
            let responseData = response.data;
            if (typeof responseData === 'string') {
                try {
                    responseData = JSON.parse(responseData);
                } catch (parseError) {
                    responseData = response.data;
                }
            }

            // Handle API Gateway response format
            if (responseData.body) {
                if (typeof responseData.body === 'string') {
                    responseData = JSON.parse(responseData.body);
                } else {
                    responseData = responseData.body;
                }
            }

            // Check if import was successful
            if (responseData.success === true) {
                setImportResults(responseData.results);
                toast.success(responseData.message || "Import completed successfully", { autoClose: 2000 });
                // Reset file input after successful import
                setSelectedFile(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            } else {
                toast.error(responseData.message || "Import failed", { autoClose: 2000 });
            }

        } catch (error) {
            console.error("Import error:", error);
            if (error.response && error.response.data) {
                let errorData = error.response.data;
                if (typeof errorData === 'string') {
                    try {
                        errorData = JSON.parse(errorData);
                    } catch (e) {
                        // Use error message as is
                    }
                }
                toast.error(errorData.message || "An error occurred during import", { autoClose: 2000 });
            } else {
                toast.error("Network error. Please check your connection and try again.", { autoClose: 2000 });
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <div className="content-header">
                <div className="container-fluid">
                <div className="row mb-2 align-items-center">
                <div className="col-6 col-sm-6">
                            <h1 className="m-0 txt_lft">CSV Data</h1>
                        </div>
                        <div className="col-6 col-sm-6">
                            <ol className="breadcrumb justify-content-end">
                                <li className="breadcrumb-item"><Link to="/soConverter"><i className="fa-solid fa-arrow-left-long" /> Back</Link></li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="frmwdth_cntr_640">
                                <div className="col-lg-12">
                                    <div className="card card-primary card-outline">
                                        <div className="card-header">
                                            <h3 className="card-title">Upload CSV File</h3>
                                        </div>
                                        <form className="form-horizontal" onSubmit={handleSubmit}>
                                            <div className="card-body">
                                                <div className="form-group">
                                                    <div className="input-group">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={selectedFile ? selectedFile.name : ''}
                                                            placeholder="Choose file"
                                                            readOnly
                                                        />
                                                        <div className="input-group-append">
                                                            <label className="btn btn-blue mb-0" htmlFor="soCsv" style={{ cursor: 'pointer' }}>
                                                                Browse
                                                            </label>
                                                            <input
                                                                ref={fileInputRef}
                                                                type="file"
                                                                className="d-none"
                                                                id="soCsv"
                                                                accept=".csv"
                                                                onChange={handleFileChange}
                                                            />
                                                        </div>
                                                    </div>
                                                    <small className="form-text text-muted mt-2">
                                                        Only CSV files (.csv) are supported for SO data upload.
                                                    </small>
                                                </div>
                                            </div>
                                            {/* /.card-body */}
                                            <div className="card-footer text-center">
                                                {isLoading ? (
                                                    <div className="d-flex justify-content-center align-items-center">
                                                        <PulseLoader color="#007bff" size={10} />
                                                        <span className="ml-2">Importing...</span>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="submit"
                                                        className="btn btn-blue"
                                                        disabled={!selectedFile || isLoading}
                                                    >
                                                        Upload
                                                    </button>
                                                )}
                                            </div>
                                        </form>
                                    </div>

                                    {/* Import Results */}
                                    {importResults && (
                                        <div className="card card-primary card-outline mt-3">
                                            <div className="card-header">
                                                <h3 className="card-title">Import Results</h3>
                                            </div>
                                            <div className="card-body import-results">
                                                <div className="row mb-3">
                                                    <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
                                                        <div className="info-box">
                                                            <span className="info-box-icon bg-info elevation-1"><i className="fa-solid fa-list"></i></span>
                                                            <div className="info-box-content">
                                                                <span className="info-box-text">Total Records</span>
                                                                <span className="info-box-number">{importResults.total || 0}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
                                                        <div className="info-box">
                                                            <span className="info-box-icon bg-success elevation-1"><i className="fa-solid fa-check"></i></span>
                                                            <div className="info-box-content">
                                                                <span className="info-box-text">Imported Records</span>
                                                                <span className="info-box-number">{importResults.imported || 0}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
                                                        <div className="info-box">
                                                            <span className="info-box-icon bg-warning elevation-1"><i className="fa-solid fa-exclamation"></i></span>
                                                            <div className="info-box-content">
                                                                <span className="info-box-text">Skipped Records</span>
                                                                <span className="info-box-number">{importResults.skipped || 0}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
                                                        <div className="info-box">
                                                            <span className="info-box-icon bg-secondary elevation-1"><i className="fa-solid fa-copy"></i></span>
                                                            <div className="info-box-content">
                                                                <span className="info-box-text">Duplicate Records</span>
                                                                <span className="info-box-number">{importResults.duplicate || 0}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
                                                        <div className="info-box">
                                                            <span className="info-box-icon bg-danger elevation-1"><i className="fa-solid fa-times"></i></span>
                                                            <div className="info-box-content">
                                                                <span className="info-box-text">Invalid Records</span>
                                                                <span className="info-box-number">{importResults.invalid || 0}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
                                                        <div className="info-box">
                                                            <span className="info-box-icon bg-danger elevation-1"><i className="fa-solid fa-exclamation-triangle"></i></span>
                                                            <div className="info-box-content">
                                                                <span className="info-box-text">Failed Records</span>
                                                                <span className="info-box-number">{importResults.failed || 0}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Imported Records */}
                                                {importResults.importedRecords && importResults.importedRecords.length > 0 && (
                                                    <div className="mb-4">
                                                        <h5 className="mb-3 text-success">Imported Records:</h5>
                                                        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6' }}>
                                                            <table className="table table-bordered table-striped mb-0">
                                                                <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f8f9fa' }}>
                                                                    <tr>
                                                                        <th>Row</th>
                                                                        <th>Item ID</th>
                                                                        <th>Item Name</th>
                                                                        <th>Material Code</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {importResults.importedRecords.map((record, index) => (
                                                                        <tr key={index}>
                                                                            <td>{record.row}</td>
                                                                            <td>{record.itemId || '-'}</td>
                                                                            <td>{record.itemName || '-'}</td>
                                                                            <td>{record.materialCode || '-'}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Skipped Records (Duplicates) */}
                                                {importResults.skippedRecords && importResults.skippedRecords.length > 0 && (
                                                    <div className="mb-4">
                                                        <h5 className="mb-3 text-warning">Skipped Records (Duplicates):</h5>
                                                        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6' }}>
                                                            <table className="table table-bordered table-striped mb-0">
                                                                <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f8f9fa' }}>
                                                                    <tr>
                                                                        <th>Row</th>
                                                                        <th>Item ID</th>
                                                                        <th>Item Name</th>
                                                                        <th>Reason</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {importResults.skippedRecords.map((record, index) => (
                                                                        <tr key={index}>
                                                                            <td>{record.row}</td>
                                                                            <td>{record.itemId || '-'}</td>
                                                                            <td>{record.itemName || '-'}</td>
                                                                            <td className="text-warning">{record.reason || '-'}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Errors */}
                                                {importResults.errors && importResults.errors.length > 0 && (
                                                    <div>
                                                        <h5 className="mb-3 text-danger">Errors:</h5>
                                                        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6' }}>
                                                            <table className="table table-bordered table-striped mb-0">
                                                                <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f8f9fa' }}>
                                                                    <tr>
                                                                        <th>Row</th>
                                                                        <th>Error</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {importResults.errors.map((error, index) => (
                                                                        <tr key={index}>
                                                                            <td>{error.row}</td>
                                                                            <td className="text-danger">{error.error}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SoData

